import React from 'react';
import { FormattedNumber, IntlShape } from 'react-intl';
import { ColumnDef } from '@tanstack/react-table';
import { SimpleSelectOption } from '../../components/atoms/Fields/Select/templates/SimpleSelect/types';
import { makeDateCellRenderer } from '../../utils/tableRenderers';
import { StatementsTableItem } from './types';
import { IconArrowForward, IconDownload } from '../../assets/svg/icons';
import styled from 'styled-components';

/* ─── Statement column cell ─── */
const StyledStatementId = styled.div`
  display: flex;
  align-items: center;
  gap: var(--s-2);
`;

const StyledStatementArrow = styled.span<{ $negative?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: var(--r-sm);
  background: ${({ $negative }) => ($negative ? 'var(--warning-soft)' : 'var(--success-soft)')};
  color: ${({ $negative }) => ($negative ? 'var(--warning-ink)' : 'var(--success-ink)')};
  flex-shrink: 0;

  svg {
    width: 14px;
    height: 14px;
    fill: currentColor;
  }
`;

const StyledStatementInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledStatementNum = styled.span`
  font-size: var(--t-md);
  font-weight: 700;
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
  color: var(--ink);
  letter-spacing: -0.01em;
`;

const StyledStatementType = styled.span`
  font-size: var(--t-xs);
  color: var(--muted);
  margin-top: 1px;
`;

/* ─── Status badge ─── */
const StyledStatusBadge = styled.span<{ $variant: 'success' | 'warning' | 'muted' }>`
  display: inline-flex;
  align-items: center;
  gap: var(--s-1);
  padding: var(--s-1) var(--s-2);
  border-radius: 999px;
  font-size: 11.5px;
  font-weight: var(--weight-semibold);
  letter-spacing: 0.01em;
  white-space: nowrap;

  background: ${({ $variant }) => {
    if ($variant === 'success') return 'var(--success-soft)';
    if ($variant === 'warning') return 'var(--warning-soft)';
    return 'rgba(9,15,56,0.05)';
  }};
  color: ${({ $variant }) => {
    if ($variant === 'success') return 'var(--success-ink)';
    if ($variant === 'warning') return 'var(--warning-ink)';
    return 'var(--muted)';
  }};

  &::before {
    content: '';
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: currentColor;
    flex-shrink: 0;
  }
`;

/* ─── Value cell ─── */
const StyledValueCell = styled.div<{ $negative?: boolean }>`
  font-size: var(--t-sm);
  font-weight: var(--weight-semibold);
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
  color: ${({ $negative }) => ($negative ? 'var(--danger-ink)' : 'var(--ink)')};
`;

/* ─── Download buttons row ─── */
const StyledDownloadsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const pillBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 5,
  padding: '5px 10px', borderRadius: 'var(--r-sm)',
  border: '1px solid var(--line)', background: 'var(--surface)',
  cursor: 'pointer', fontFamily: 'inherit', fontSize: 'var(--t-xs)', fontWeight: 600,
  color: 'var(--ink-2)', height: 28,
};

const pillBtnGreenStyle: React.CSSProperties = {
  ...pillBtnStyle,
  background: 'var(--brand-green)', color: 'var(--surface)', borderColor: 'var(--brand-green)',
};

/* ─── Fake data ─── */
export const statementsTableData: StatementsTableItem[] = [
  {
    statusCol: 'Statement',
    valueIqdCol: 25000,
    dateCol: '01/02/2020',
    statementIdCol: 587458656,
    downloadCol: { pdfUrlAr: 'download.url' },
  },
  {
    statusCol: 'Statement',
    valueIqdCol: 25000,
    dateCol: '01/02/2020',
    statementIdCol: 587458656,
    downloadCol: { pdfUrlAr: 'download.url' },
  },
  {
    statusCol: 'Statement',
    valueIqdCol: 25000,
    dateCol: '01/02/2020',
    statementIdCol: 587458656,
    downloadCol: { pdfUrlAr: 'download.url' },
  },
  {
    statusCol: 'Statement',
    valueIqdCol: 25000,
    dateCol: '01/02/2020',
    statementIdCol: 587458656,
    downloadCol: { pdfUrlAr: 'download.url' },
  },
  {
    statusCol: 'Statement',
    valueIqdCol: 25000,
    dateCol: '01/02/2020',
    statementIdCol: 587458656,
    downloadCol: { pdfUrlAr: 'download.url' },
  },
  {
    statusCol: 'Statement',
    valueIqdCol: 25000,
    dateCol: '01/02/2020',
    statementIdCol: 587458656,
    downloadCol: { pdfUrlAr: 'download.url' },
  },
  {
    statusCol: 'Statement',
    valueIqdCol: 25000,
    dateCol: '01/02/2020',
    statementIdCol: 587458656,
    downloadCol: { pdfUrlAr: 'download.url' },
  },
  {
    statusCol: 'Statement',
    valueIqdCol: 25000,
    dateCol: '01/02/2020',
    statementIdCol: 587458656,
    downloadCol: { pdfUrlAr: 'download.url' },
  },
  {
    statusCol: 'Statement',
    valueIqdCol: 25000,
    dateCol: '01/02/2020',
    statementIdCol: 587458656,
    downloadCol: { pdfUrlAr: 'download.url' },
  },
  {
    statusCol: 'Statement',
    valueIqdCol: 25000,
    dateCol: '01/02/2020',
    statementIdCol: 587458656,
    downloadCol: { pdfUrlAr: 'download.url' },
  },
  {
    statusCol: 'Statement',
    valueIqdCol: 25000,
    dateCol: '01/02/2020',
    statementIdCol: 587458656,
    downloadCol: { pdfUrlAr: 'download.url' },
  },
  {
    statusCol: 'Statement',
    valueIqdCol: 25000,
    dateCol: '01/02/2020',
    statementIdCol: 587458656,
    downloadCol: { pdfUrlAr: 'download.url' },
  },
  {
    statusCol: 'Statement',
    valueIqdCol: 25000,
    dateCol: '01/02/2020',
    statementIdCol: 587458656,
    downloadCol: { pdfUrlAr: 'download.url' },
  },
];

export const statementsTableColumns = (
  intl: IntlShape
): ColumnDef<StatementsTableItem>[] => [
  {
    header: intl.formatMessage({
      id: 'financials.statementId',
      defaultMessage: 'Statement',
    }),
    accessorKey: 'statementIdCol',
    cell: ({ row: { original } }) => (
      <StyledStatementId>
        <StyledStatementArrow $negative={original.valueIqdCol < 0}>
          <IconArrowForward />
        </StyledStatementArrow>
        <StyledStatementInfo>
          <StyledStatementNum>#{original.statementIdCol}</StyledStatementNum>
          <StyledStatementType>{original.valueIqdCol < 0 ? intl.formatMessage({ id: 'financials.statementSubCashOut', defaultMessage: 'Cash out' }) : intl.formatMessage({ id: 'financials.statementSubIssued', defaultMessage: 'Statement issued' })}</StyledStatementType>
        </StyledStatementInfo>
      </StyledStatementId>
    ),
  },
  {
    header: intl.formatMessage({
      id: 'orders.date',
      defaultMessage: 'Date',
    }),
    accessorKey: 'dateCol',
    cell: makeDateCellRenderer(),
  },
  {
    header: intl.formatMessage({
      id: 'financials.status',
      defaultMessage: 'Status',
    }),
    accessorKey: 'statusCol',
    cell: ({ cell: { getValue } }) => {
      const status = getValue() as string;
      return (
        <StyledStatusBadge $variant='success'>
          {intl.formatMessage({ id: 'financials.issued', defaultMessage: 'Issued' })}
        </StyledStatusBadge>
      );
    },
  },
  {
    header: intl.formatMessage({
      id: 'orders.valueIQD',
      defaultMessage: 'Value IQD',
    }),
    accessorKey: 'valueIqdCol',
    cell: ({ cell: { getValue } }) => {
      const v = getValue() as number;
      return (
        <StyledValueCell $negative={v < 0}>
          <FormattedNumber value={v} /> IQD
        </StyledValueCell>
      );
    },
  },
  {
    accessorKey: 'downloadCol',
    header: intl.formatMessage({
      id: 'financials.downloads',
      defaultMessage: 'Downloads',
    }),
    cell: ({ cell: { getValue } }) => {
      const statementUrls = getValue() as StatementsTableItem['downloadCol'];

      return (
        <StyledDownloadsRow>
          {statementUrls.pdfUrlAr && (
            <a href={statementUrls.pdfUrlAr} download style={{ textDecoration: 'none' }}>
              <button style={pillBtnStyle}><IconDownload style={{ width: 12, height: 12 }} /><span>AR</span></button>
            </a>
          )}
          {statementUrls.pdfUrlEn && (
            <a href={statementUrls.pdfUrlEn} download style={{ textDecoration: 'none' }}>
              <button style={pillBtnStyle}><IconDownload style={{ width: 12, height: 12 }} /><span>EN</span></button>
            </a>
          )}
          {statementUrls.excelUrl && (
            <a href={statementUrls.excelUrl} download style={{ textDecoration: 'none' }}>
              <button style={pillBtnGreenStyle}><IconDownload style={{ width: 12, height: 12 }} /><span>Excel</span></button>
            </a>
          )}
        </StyledDownloadsRow>
      );
    },
  },
];

export const yearDropdownOptions: SimpleSelectOption[] = Array.from(
  {
    length: 25,
  },
  (_, index) => {
    const year = (index + 2021).toString();

    return {
      label: year,
      value: year,
    };
  }
);
